import { Cause, Clock, Effect, Queue, Stream } from "effect";
import { HttpClient, HttpClientRequest } from "effect/unstable/http";

import { apiBase, statusError } from "../../api/client";
import { toMessage } from "../../api/errors";
import { buildUrl } from "../../api/url";
import { ClerkTokenService } from "../../auth/tokenService";
import type { Api } from "../shared";
import { playBeep } from "./audio";
import { Message } from "./message";

/**
 * v1 `setupAICall` as a Subscription stream (KTD12): gated on the page's
 * `session` slice, it asks for the microphone, opens the WebRTC peer
 * connection through `POST /rtc-connect`, and emits `SessionConnected`,
 * `ToolCalled`, or `SessionFailed`. Tearing the Subscription down (the slice
 * going to `None`: a click, the countdown, leaving the page) closes the
 * channel and the peer connection, stops the tracks, and plays the beep.
 * Browser-only by nature; not exercised by Story or Scene tests.
 */

const TOOLS = [
	{
		type: "function",
		name: "addNode",
		description: "Adds a record (person) to the app",
		parameters: {
			type: "object",
			properties: {
				name: { type: "string", description: "The name of the person" },
				description: {
					type: "string",
					description: "A description of the person",
				},
				tags: {
					type: "array",
					items: { type: "string" },
					description: "An array of tags for the person",
				},
			},
		},
	},
	{
		type: "function",
		name: "addKnot",
		description: "Adds a entry (message) to the app",
		parameters: {
			type: "object",
			properties: {
				nodeName: {
					type: "string",
					description: "The name of the record (person) to add a note too",
				},
				note: {
					type: "string",
					description: "The note that is going to be added to the person",
				},
				tags: {
					type: "array",
					items: { type: "string" },
					description: "An array of tags for the person",
				},
			},
		},
	},
	{
		type: "function",
		name: "editNode",
		description: "Updates details for an existing record (person)",
		parameters: {
			type: "object",
			properties: {
				name: {
					type: "string",
					description: "The current name of the person to update",
				},
				newName: { type: "string", description: "The new name for the person" },
				description: {
					type: "string",
					description: "The updated description for the person",
				},
				tags: {
					type: "array",
					items: { type: "string" },
					description: "An updated array of tags for the person",
				},
			},
		},
	},
	{
		type: "function",
		name: "editKnot",
		description: "Updates the details of an existing entry (note)",
		parameters: {
			type: "object",
			properties: {
				knotId: {
					type: "string",
					description: "The ID of the entry to update",
				},
				title: { type: "string", description: "The new title for the entry" },
				description: {
					type: "string",
					description: "The new description for the entry",
				},
				tags: {
					type: "array",
					items: { type: "string" },
					description: "Updated tags for the entry",
				},
			},
		},
	},
	{
		type: "function",
		name: "navigateToPerson",
		description: "Go to a person in the app",
		parameters: {
			type: "object",
			properties: {
				name: {
					type: "string",
					description: "The name of the person to search for",
				},
			},
		},
	},
];

// ponytail: module-level slot for the one open data channel so `SendToolResult` can reach it; one session is ever open.
let current: { id: string; channel: RTCDataChannel } | undefined;

/** v1: the function result goes back over the data channel as a `function_call_output` item. */
export const sendToolResult = (id: string, callId: string, output: string) =>
	Effect.sync(() => {
		if (current?.id !== id) return;
		current.channel.send(
			JSON.stringify({
				type: "conversation.item.create",
				item: { type: "function_call_output", call_id: callId, output },
			}),
		);
	});

const describe = (error: unknown): string =>
	error instanceof Error ? error.message : "An unknown error occurred";

export const session = (id: string): Stream.Stream<Message, never, Api> =>
	Stream.callback<Message, never, Api>((queue) =>
		Effect.gen(function* () {
			const emit = (message: Message) => {
				Queue.offerUnsafe(queue, message);
			};
			const client = yield* HttpClient.HttpClient;
			const clock = yield* Clock.Clock;
			const token = yield* (yield* ClerkTokenService).getToken;
			if (apiBase() === "") {
				return yield* Effect.fail(
					new Error("VITE_API_URL environment variable is not set"),
				);
			}
			const media = yield* Effect.tryPromise({
				try: () => navigator.mediaDevices.getUserMedia({ audio: true }),
				catch: () =>
					new Error(
						"Microphone access is required. Please allow microphone access and try again.",
					),
			});
			const peer = new RTCPeerConnection();
			const channel = peer.createDataChannel("response");
			yield* Effect.addFinalizer(() =>
				Effect.sync(() => {
					if (current?.id === id) current = undefined;
					channel.close();
					peer.close();
					for (const track of media.getTracks()) track.stop();
					playBeep(698.46, 0.2, 0.2);
				}),
			);
			channel.addEventListener("open", () => {
				channel.send(
					JSON.stringify({
						type: "session.update",
						session: { modalities: ["text", "audio"], tools: TOOLS },
					}),
				);
				emit(
					Message.SessionConnected({ id, at: clock.currentTimeMillisUnsafe() }),
				);
			});
			channel.addEventListener("message", (event) => {
				try {
					const msg = JSON.parse(event.data);
					if (msg.type === "response.function_call_arguments.done") {
						emit(
							Message.ToolCalled({
								id,
								callId: String(msg.call_id ?? ""),
								name: String(msg.name ?? ""),
								args: String(msg.arguments ?? "{}"),
							}),
						);
					}
				} catch {
					// v1 logged and ignored unparseable channel messages.
				}
			});
			peer.addEventListener("connectionstatechange", () => {
				if (peer.connectionState === "failed") {
					emit(
						Message.SessionFailed({ id, error: "WebRTC connection failed" }),
					);
				}
			});
			for (const track of media.getTracks()) {
				peer.addTransceiver(track, { direction: "sendrecv" });
			}
			const offer = yield* Effect.promise(() => peer.createOffer());
			yield* Effect.promise(() => peer.setLocalDescription(offer));
			const response = yield* client.execute(
				HttpClientRequest.make("POST")(buildUrl(apiBase(), "/rtc-connect"), {
					headers: { Authorization: `Bearer ${token}` },
				}).pipe(HttpClientRequest.bodyText(offer.sdp ?? "", "application/sdp")),
			);
			if (response.status >= 400) {
				const body = yield* response.json.pipe(
					Effect.orElseSucceed(() => null),
				);
				return yield* Effect.fail(
					new Error(toMessage(statusError(response.status, body))),
				);
			}
			const answer = yield* response.text;
			yield* Effect.promise(() =>
				peer.setRemoteDescription({ sdp: answer, type: "answer" }),
			);
			current = { id, channel };
		}).pipe(
			Effect.catchCause((cause) =>
				Effect.sync(() => {
					Queue.offerUnsafe(
						queue,
						Message.SessionFailed({ id, error: describe(Cause.squash(cause)) }),
					);
				}),
			),
		),
	);
